import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

const DARK_BG = {
  background: "radial-gradient(ellipse at top, #3D0810 0%, #1a0205 40%, #0f0102 100%)",
};
const GOLD = "#F1E194";
const CREAM = "#FDF6E3";
const BORDER = "rgba(241,225,148,0.18)";
const CARD_BG = "rgba(91,14,20,0.45)";
const SIDEBAR_BG = "rgba(15,1,2,0.85)";

// ─── Auth helpers ────────────────────────────────────────────────────────────
function getToken() {
  return localStorage.getItem("studio_token");
}
function getUser() {
  try {
    return JSON.parse(localStorage.getItem("studio_user") || "null");
  } catch {
    return null;
  }
}
function authHeaders() {
  return { Authorization: `Bearer ${getToken()}` };
}

// ─── API wrapper with 401 handling ───────────────────────────────────────────
async function apiGet(path, onUnauth) {
  try {
    const { data } = await axios.get(`${API}${path}`, { headers: authHeaders() });
    return { data, error: null };
  } catch (e) {
    if (e?.response?.status === 401) { onUnauth(); return { data: null, error: "401" }; }
    return { data: null, error: e?.response?.data?.detail || "خطأ في الاتصال" };
  }
}
async function apiPost(path, body, onUnauth) {
  try {
    const { data } = await axios.post(`${API}${path}`, body, { headers: authHeaders() });
    return { data, error: null };
  } catch (e) {
    if (e?.response?.status === 401) { onUnauth(); return { data: null, error: "401" }; }
    return { data: null, error: e?.response?.data?.detail || "خطأ في الاتصال" };
  }
}

// ─── Inline Login ─────────────────────────────────────────────────────────────
function InlineLogin({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      toast.error("أدخل اسم المستخدم وكلمة المرور");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/studio/auth/login`, {
        username: username.trim(),
        password,
      });
      localStorage.setItem("studio_token", data.token);
      localStorage.setItem("studio_user", JSON.stringify({
        name: data.name,
        role: data.role,
        studio_role: data.studio_role,
      }));
      toast.success(`أهلاً ${data.name}!`);
      onSuccess({ name: data.name, role: data.role, studio_role: data.studio_role });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "بيانات الدخول غير صحيحة");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={DARK_BG}
      dir="rtl"
    >
      <div className="w-full max-w-sm">
        <div
          className="rounded-3xl p-8 text-center"
          style={{
            background: CARD_BG,
            border: `1px solid ${BORDER}`,
            backdropFilter: "blur(12px)",
            boxShadow: "0 0 60px rgba(91,14,20,0.4)",
          }}
        >
          <div style={{ fontSize: 38, marginBottom: 4 }}>✦</div>
          <h1 className="text-3xl font-black mb-1" style={{ color: GOLD }}>HUJJAH Studio</h1>
          <p className="text-sm mb-8" style={{ color: "rgba(241,225,148,0.5)" }}>بوابة فريق المحتوى</p>

          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="اسم المستخدم"
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            className="w-full px-4 py-3 rounded-xl text-sm font-bold outline-none text-center mb-3"
            style={{
              background: "rgba(26,2,5,0.6)",
              border: `2px solid ${BORDER}`,
              color: GOLD,
              transition: "border-color 0.2s",
            }}
          />

          <div className="relative mb-6">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="كلمة المرور"
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              className="w-full px-4 py-3 rounded-xl text-lg font-bold outline-none text-center"
              style={{
                background: "rgba(26,2,5,0.6)",
                border: `2px solid ${BORDER}`,
                color: GOLD,
              }}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-sm"
              style={{ color: "rgba(241,225,148,0.4)" }}
            >
              {showPw ? "🙈" : "👁"}
            </button>
          </div>

          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full font-black text-lg py-3 rounded-full transition-all duration-300 disabled:opacity-50"
            style={{ background: GOLD, color: "#1a0205" }}
          >
            {loading ? "جاري الدخول..." : "دخول ✦"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, highlight }) {
  return (
    <div
      className="rounded-2xl p-5 flex flex-col items-center gap-1 transition-all duration-200"
      style={{
        background: highlight ? `rgba(241,225,148,0.12)` : CARD_BG,
        border: `1px solid ${highlight ? "rgba(241,225,148,0.4)" : BORDER}`,
        boxShadow: highlight ? "0 0 20px rgba(241,225,148,0.08)" : undefined,
      }}
    >
      <div style={{ fontSize: 26 }}>{icon}</div>
      <div
        className="text-3xl font-black tabular-nums"
        style={{ color: highlight ? GOLD : CREAM }}
      >
        {value ?? "—"}
      </div>
      <div className="text-xs font-semibold" style={{ color: "rgba(241,225,148,0.55)" }}>
        {label}
      </div>
    </div>
  );
}

// ─── Action icon map ───────────────────────────────────────────────────────────
const ACTION_ICONS = {
  create: "➕",
  edit: "✏️",
  delete: "🗑️",
  approve: "✅",
  reject: "❌",
  upload: "📤",
  category: "🗂️",
  question: "❓",
  image: "🖼️",
  audio: "🎵",
};
function actionIcon(action = "") {
  const key = Object.keys(ACTION_ICONS).find((k) => action.toLowerCase().includes(k));
  return key ? ACTION_ICONS[key] : "📋";
}

const ROLE_LABELS = {
  super_admin: "مدير رئيسي",
  admin: "مدير",
  editor: "محرر",
  reviewer: "مراجع",
  content: "محتوى",
};
function roleBadge(role) {
  const colors = {
    super_admin: { bg: "rgba(241,225,148,0.2)", text: GOLD },
    admin: { bg: "rgba(200,100,100,0.2)", text: "#f8a0a0" },
    editor: { bg: "rgba(100,180,200,0.2)", text: "#90d0e0" },
    reviewer: { bg: "rgba(100,200,150,0.2)", text: "#90dbb0" },
    content: { bg: "rgba(180,150,220,0.2)", text: "#c8a8e8" },
  };
  const c = colors[role] || { bg: "rgba(255,255,255,0.1)", text: CREAM };
  return (
    <span
      className="text-xs font-bold px-2 py-0.5 rounded-full"
      style={{ background: c.bg, color: c.text }}
    >
      {ROLE_LABELS[role] || role}
    </span>
  );
}

function formatTime(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleString("ar-SA", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

// ─── TABS ──────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "home", label: "الرئيسية", icon: "🏠" },
  { id: "chat", label: "المساعد الذكي", icon: "🤖" },
  { id: "audit", label: "سجل العمليات", icon: "📋" },
  { id: "leaderboard", label: "المتصدرون", icon: "🏆" },
  { id: "obsidian", label: "Obsidian", icon: "🟣" },
];

const STARTER_PROMPTS = [
  "أنشئ 10 أسئلة عن الرياضة",
  "راجع الأخطاء الإملائية",
  "اقترح صور مناسبة",
  "تحقق من التكرار",
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function StudioPage() {
  const [authed, setAuthed] = useState(!!getToken());
  const [user, setUser] = useState(getUser());
  const [activeTab, setActiveTab] = useState("home");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Home
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [recentAudit, setRecentAudit] = useState([]);

  // AI Chat
  const [messages, setMessages] = useState([
    { role: "assistant", content: "مرحباً! أنا مساعدك الذكي في استوديو حُجّة. كيف يمكنني مساعدتك اليوم؟" },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Audit
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSkip, setAuditSkip] = useState(0);
  const [auditHasMore, setAuditHasMore] = useState(true);

  // Leaderboard
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbLoading, setLbLoading] = useState(false);

  // Obsidian
  const [obsidianStatus, setObsidianStatus] = useState(null);
  const [obsidianLoading, setObsidianLoading] = useState(false);
  const [flushLoading, setFlushLoading] = useState(false);

  const handleUnauth = useCallback(() => {
    localStorage.removeItem("studio_token");
    localStorage.removeItem("studio_user");
    setAuthed(false);
    setUser(null);
    toast.error("انتهت الجلسة — يرجى إعادة الدخول");
  }, []);

  // ── Load stats ──────────────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    const { data } = await apiGet("/studio/stats", handleUnauth);
    if (data) setStats(data);
    setStatsLoading(false);
  }, [handleUnauth]);

  const loadRecentAudit = useCallback(async () => {
    const { data } = await apiGet("/studio/audit?limit=5&skip=0", handleUnauth);
    if (data) setRecentAudit(Array.isArray(data) ? data : []);
  }, [handleUnauth]);

  // ── Load audit log ──────────────────────────────────────────────────────────
  const loadAudit = useCallback(async (skip = 0, append = false) => {
    setAuditLoading(true);
    const { data } = await apiGet(`/studio/audit?limit=20&skip=${skip}`, handleUnauth);
    if (data) {
      const arr = Array.isArray(data) ? data : [];
      setAuditLog((prev) => (append ? [...prev, ...arr] : arr));
      setAuditHasMore(arr.length === 20);
    }
    setAuditLoading(false);
  }, [handleUnauth]);

  // ── Load leaderboard ────────────────────────────────────────────────────────
  const loadLeaderboard = useCallback(async () => {
    setLbLoading(true);
    const { data } = await apiGet("/studio/leaderboard", handleUnauth);
    if (data) setLeaderboard(Array.isArray(data) ? data : []);
    setLbLoading(false);
  }, [handleUnauth]);

  // ── Load obsidian ───────────────────────────────────────────────────────────
  const loadObsidian = useCallback(async () => {
    setObsidianLoading(true);
    const { data } = await apiGet("/studio/obsidian/status", handleUnauth);
    if (data) setObsidianStatus(data);
    setObsidianLoading(false);
  }, [handleUnauth]);

  // ── Tab switching with lazy loads ───────────────────────────────────────────
  useEffect(() => {
    if (!authed) return;
    if (activeTab === "home") {
      loadStats();
      loadRecentAudit();
    } else if (activeTab === "audit") {
      setAuditSkip(0);
      loadAudit(0, false);
    } else if (activeTab === "leaderboard") {
      loadLeaderboard();
    } else if (activeTab === "obsidian") {
      loadObsidian();
    }
  }, [activeTab, authed]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chat scroll ─────────────────────────────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatLoading]);

  // ── Send chat ────────────────────────────────────────────────────────────────
  const sendChat = useCallback(
    async (msg) => {
      const text = msg ?? chatInput;
      if (!text.trim() || chatLoading) return;
      const userMsg = { role: "user", content: text.trim() };
      const history = messages.filter((m) => m.role !== "typing");
      setMessages((prev) => [...prev.filter((m) => m.role !== "typing"), userMsg]);
      setChatInput("");
      setChatLoading(true);
      setMessages((prev) => [...prev, { role: "typing", content: "..." }]);

      const { data, error } = await apiPost(
        "/studio/ai/chat",
        { message: text.trim(), history: history.map((m) => ({ role: m.role, content: m.content })) },
        handleUnauth
      );
      setMessages((prev) => prev.filter((m) => m.role !== "typing"));
      if (data) {
        setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      } else if (error && error !== "401") {
        toast.error("خطأ في المساعد: " + error);
      }
      setChatLoading(false);
    },
    [chatInput, chatLoading, messages, handleUnauth]
  );

  // ── Flush obsidian ───────────────────────────────────────────────────────────
  const flushObsidian = async () => {
    setFlushLoading(true);
    const { data, error } = await apiPost("/studio/obsidian/flush", {}, handleUnauth);
    if (data) {
      toast.success("تم إرسال طلب المزامنة");
      loadObsidian();
    } else if (error && error !== "401") {
      toast.error("خطأ في المزامنة: " + error);
    }
    setFlushLoading(false);
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setAuthed(true);
  };

  // ── Logout ───────────────────────────────────────────────────────────────────
  const logout = () => {
    localStorage.removeItem("studio_token");
    localStorage.removeItem("studio_user");
    setAuthed(false);
    setUser(null);
    setMessages([
      { role: "assistant", content: "مرحباً! أنا مساعدك الذكي في استوديو حُجّة. كيف يمكنني مساعدتك اليوم؟" },
    ]);
  };

  // ── Not authenticated ────────────────────────────────────────────────────────
  if (!authed) {
    return <InlineLogin onSuccess={handleLoginSuccess} />;
  }

  // ═══════════════════════════════ RENDER TABS ══════════════════════════════════

  // HOME TAB
  const renderHome = () => (
    <div className="space-y-6">
      {/* Welcome banner */}
      <div
        className="rounded-2xl p-6"
        style={{
          background: "linear-gradient(135deg, rgba(91,14,20,0.7) 0%, rgba(61,8,16,0.9) 100%)",
          border: `1px solid ${BORDER}`,
          boxShadow: "0 0 30px rgba(241,225,148,0.05)",
        }}
      >
        <h2 className="text-2xl font-black" style={{ color: GOLD }}>
          مرحباً {user?.name || "بك"} ✦
        </h2>
        <p className="text-sm mt-1" style={{ color: "rgba(253,246,227,0.6)" }}>
          {user?.studio_role ? ROLE_LABELS[user.studio_role] || user.studio_role : ""}
          {" — "}
          استوديو حُجّة للمحتوى
        </p>
      </div>

      {/* Main stats */}
      {statsLoading ? (
        <div className="text-center py-8" style={{ color: "rgba(241,225,148,0.4)" }}>
          جاري تحميل الإحصائيات...
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="اليوم" value={stats.today} icon="📅" />
            <StatCard label="هذا الأسبوع" value={stats.week} icon="📆" />
            <StatCard label="هذا الشهر" value={stats.month} icon="🗓️" />
            <StatCard label="الإجمالي" value={stats.all_time} icon="🏅" highlight />
          </div>

          {stats.by_action && Object.keys(stats.by_action).length > 0 && (
            <div
              className="rounded-2xl p-5"
              style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
            >
              <h3 className="font-black mb-4" style={{ color: GOLD }}>
                التفاصيل حسب النوع
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(stats.by_action).map(([action, count]) => (
                  <div
                    key={action}
                    className="flex items-center gap-2 rounded-xl px-3 py-2"
                    style={{ background: "rgba(26,2,5,0.4)", border: `1px solid ${BORDER}` }}
                  >
                    <span style={{ fontSize: 18 }}>{actionIcon(action)}</span>
                    <div>
                      <div className="text-sm font-bold" style={{ color: CREAM }}>{count}</div>
                      <div className="text-xs" style={{ color: "rgba(241,225,148,0.45)" }}>{action}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : null}

      {/* Recent activity */}
      <div
        className="rounded-2xl p-5"
        style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
      >
        <h3 className="font-black mb-4" style={{ color: GOLD }}>آخر نشاط</h3>
        {recentAudit.length === 0 ? (
          <p className="text-sm" style={{ color: "rgba(241,225,148,0.35)" }}>لا يوجد نشاط حديث</p>
        ) : (
          <div className="space-y-2">
            {recentAudit.map((entry, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-xl px-3 py-2"
                style={{ background: "rgba(26,2,5,0.35)" }}
              >
                <span style={{ fontSize: 18 }}>{actionIcon(entry.action)}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color: CREAM }}>
                    {entry.action}
                    {entry.target_name && (
                      <span style={{ color: "rgba(241,225,148,0.5)" }}> · {entry.target_name}</span>
                    )}
                  </div>
                  <div className="text-xs" style={{ color: "rgba(241,225,148,0.35)" }}>
                    {formatTime(entry.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // AI CHAT TAB
  const renderChat = () => (
    <div className="flex flex-col h-full" style={{ height: "calc(100vh - 130px)" }}>
      {/* Starter prompts */}
      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {STARTER_PROMPTS.map((p) => (
            <button
              key={p}
              onClick={() => sendChat(p)}
              className="text-xs font-bold px-3 py-2 rounded-full transition-all duration-150"
              style={{
                background: "rgba(241,225,148,0.1)",
                border: `1px solid ${BORDER}`,
                color: GOLD,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(241,225,148,0.2)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(241,225,148,0.1)")}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto space-y-3 p-4 rounded-2xl"
        style={{
          background: "rgba(15,1,2,0.6)",
          border: `1px solid ${BORDER}`,
          minHeight: 0,
        }}
      >
        {messages.map((msg, i) => {
          const isUser = msg.role === "user";
          const isTyping = msg.role === "typing";
          return (
            <div
              key={i}
              className={`flex ${isUser ? "justify-start" : "justify-end"}`}
            >
              <div
                className="max-w-xs sm:max-w-md rounded-2xl px-4 py-3 text-sm"
                style={
                  isUser
                    ? {
                        background: "rgba(241,225,148,0.15)",
                        border: `1px solid rgba(241,225,148,0.3)`,
                        color: GOLD,
                        borderBottomRightRadius: 4,
                      }
                    : isTyping
                    ? {
                        background: "rgba(91,14,20,0.5)",
                        color: "rgba(241,225,148,0.5)",
                        borderBottomLeftRadius: 4,
                      }
                    : {
                        background: CARD_BG,
                        border: `1px solid ${BORDER}`,
                        color: CREAM,
                        borderBottomLeftRadius: 4,
                      }
                }
              >
                {isTyping ? (
                  <span className="animate-pulse">يكتب...</span>
                ) : (
                  <span style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{msg.content}</span>
                )}
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="mt-3 flex gap-2">
        <input
          type="text"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendChat()}
          placeholder="اكتب رسالتك..."
          disabled={chatLoading}
          className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold outline-none"
          style={{
            background: "rgba(26,2,5,0.7)",
            border: `2px solid ${BORDER}`,
            color: CREAM,
          }}
        />
        <button
          onClick={() => sendChat()}
          disabled={chatLoading || !chatInput.trim()}
          className="px-5 py-3 rounded-xl font-black transition-all duration-150 disabled:opacity-40"
          style={{ background: GOLD, color: "#1a0205" }}
        >
          إرسال
        </button>
      </div>
    </div>
  );

  // AUDIT LOG TAB
  const renderAudit = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-black" style={{ color: GOLD }}>سجل العمليات</h2>
      {auditLoading && auditLog.length === 0 ? (
        <div className="text-center py-8" style={{ color: "rgba(241,225,148,0.4)" }}>
          جاري التحميل...
        </div>
      ) : auditLog.length === 0 ? (
        <div className="text-center py-8" style={{ color: "rgba(241,225,148,0.3)" }}>
          لا يوجد سجل بعد
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {auditLog.map((entry, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-xl px-4 py-3 transition-all"
                style={{
                  background: CARD_BG,
                  border: `1px solid ${BORDER}`,
                }}
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(241,225,148,0.1)", fontSize: 18 }}
                >
                  {actionIcon(entry.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold" style={{ color: CREAM }}>
                      {entry.admin_name || "—"}
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(241,225,148,0.1)", color: GOLD }}
                    >
                      {entry.action}
                    </span>
                    {entry.target_name && (
                      <span className="text-xs truncate" style={{ color: "rgba(253,246,227,0.5)" }}>
                        {entry.target_type && `${entry.target_type}: `}{entry.target_name}
                      </span>
                    )}
                  </div>
                  {entry.details && (
                    <div
                      className="text-xs mt-0.5 truncate"
                      style={{ color: "rgba(241,225,148,0.35)" }}
                    >
                      {typeof entry.details === "string"
                        ? entry.details
                        : JSON.stringify(entry.details)}
                    </div>
                  )}
                </div>
                <div
                  className="text-xs flex-shrink-0"
                  style={{ color: "rgba(241,225,148,0.35)" }}
                >
                  {formatTime(entry.timestamp)}
                </div>
              </div>
            ))}
          </div>

          {auditHasMore && (
            <button
              onClick={() => {
                const newSkip = auditSkip + 20;
                setAuditSkip(newSkip);
                loadAudit(newSkip, true);
              }}
              disabled={auditLoading}
              className="w-full py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-40"
              style={{
                background: "rgba(241,225,148,0.1)",
                border: `1px solid ${BORDER}`,
                color: GOLD,
              }}
            >
              {auditLoading ? "جاري التحميل..." : "تحميل المزيد"}
            </button>
          )}
        </>
      )}
    </div>
  );

  // LEADERBOARD TAB
  const renderLeaderboard = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-black" style={{ color: GOLD }}>🏆 المتصدرون</h2>
      {lbLoading ? (
        <div className="text-center py-8" style={{ color: "rgba(241,225,148,0.4)" }}>
          جاري التحميل...
        </div>
      ) : leaderboard.length === 0 ? (
        <div className="text-center py-8" style={{ color: "rgba(241,225,148,0.3)" }}>لا يوجد بيانات</div>
      ) : (
        <div className="space-y-3">
          {leaderboard.map((person, i) => {
            const medals = ["🥇", "🥈", "🥉"];
            const rank = i + 1;
            const isTop3 = rank <= 3;
            return (
              <div
                key={i}
                className="flex items-center gap-4 rounded-2xl px-5 py-4 transition-all"
                style={{
                  background: isTop3
                    ? "linear-gradient(135deg, rgba(241,225,148,0.12) 0%, rgba(91,14,20,0.5) 100%)"
                    : CARD_BG,
                  border: `1px solid ${isTop3 ? "rgba(241,225,148,0.3)" : BORDER}`,
                  boxShadow: isTop3 ? "0 0 16px rgba(241,225,148,0.06)" : undefined,
                }}
              >
                <div
                  className="text-2xl font-black w-10 text-center"
                  style={{ color: isTop3 ? GOLD : "rgba(241,225,148,0.35)" }}
                >
                  {medals[i] || `#${rank}`}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-black text-base" style={{ color: CREAM }}>
                      {person.name}
                    </span>
                    {roleBadge(person.role)}
                  </div>
                  <div className="flex gap-3 mt-1 flex-wrap">
                    {person.questions > 0 && (
                      <span className="text-xs" style={{ color: "rgba(253,246,227,0.5)" }}>
                        ❓ {person.questions} سؤال
                      </span>
                    )}
                    {person.edits > 0 && (
                      <span className="text-xs" style={{ color: "rgba(253,246,227,0.5)" }}>
                        ✏️ {person.edits} تعديل
                      </span>
                    )}
                    {person.categories > 0 && (
                      <span className="text-xs" style={{ color: "rgba(253,246,227,0.5)" }}>
                        🗂️ {person.categories} تصنيف
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black tabular-nums" style={{ color: GOLD }}>
                    {person.total}
                  </div>
                  <div className="text-xs" style={{ color: "rgba(241,225,148,0.4)" }}>إجمالي</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // OBSIDIAN TAB
  const renderObsidian = () => {
    const s = obsidianStatus;
    const isSuperAdmin = user?.role === "super_admin" || user?.studio_role === "super_admin";

    const getStatusColor = () => {
      if (!s) return "#888";
      if (s.failed > 0) return "#f87171";
      if (s.pending > 0) return "#fbbf24";
      return "#4ade80";
    };
    const getStatusLabel = () => {
      if (!s) return "غير متاح";
      if (s.failed > 0) return "يوجد أخطاء";
      if (s.pending > 0) return "جاري المزامنة";
      return "مزامن";
    };

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-xl font-black" style={{ color: GOLD }}>🟣 Obsidian Sync</h2>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{
                background: getStatusColor(),
                boxShadow: `0 0 8px ${getStatusColor()}`,
              }}
            />
            <span className="text-sm font-bold" style={{ color: getStatusColor() }}>
              {getStatusLabel()}
            </span>
          </div>
        </div>

        {obsidianLoading ? (
          <div className="text-center py-8" style={{ color: "rgba(241,225,148,0.4)" }}>
            جاري التحميل...
          </div>
        ) : s ? (
          <>
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="انتظار" value={s.pending} icon="⏳" />
              <StatCard label="مكتمل" value={s.done} icon="✅" highlight />
              <StatCard label="فشل" value={s.failed} icon="❌" />
            </div>

            <div
              className="rounded-2xl p-4 space-y-3"
              style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: "rgba(253,246,227,0.6)" }}>
                  API مفعّل
                </span>
                <span
                  className="text-sm font-bold"
                  style={{ color: s.api_key_set ? "#4ade80" : "#f87171" }}
                >
                  {s.api_key_set ? "✅ نعم" : "❌ لا"}
                </span>
              </div>
              {s.api_url && (
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: "rgba(253,246,227,0.6)" }}>
                    عنوان API
                  </span>
                  <span
                    className="text-xs font-mono truncate max-w-xs"
                    style={{ color: "rgba(241,225,148,0.5)" }}
                  >
                    {s.api_url}
                  </span>
                </div>
              )}
            </div>

            {isSuperAdmin && (
              <button
                onClick={flushObsidian}
                disabled={flushLoading}
                className="w-full py-3 rounded-xl font-black text-sm transition-all disabled:opacity-40"
                style={{ background: "rgba(241,225,148,0.15)", border: `1px solid ${BORDER}`, color: GOLD }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(241,225,148,0.25)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(241,225,148,0.15)")}
              >
                {flushLoading ? "جاري المزامنة..." : "🔄 مزامنة الآن"}
              </button>
            )}

            <button
              onClick={loadObsidian}
              className="w-full py-2 rounded-xl text-xs transition-all"
              style={{ color: "rgba(241,225,148,0.35)", border: `1px solid ${BORDER}` }}
            >
              تحديث الحالة
            </button>
          </>
        ) : (
          <div className="text-center py-8" style={{ color: "rgba(241,225,148,0.3)" }}>
            تعذّر تحميل حالة Obsidian
          </div>
        )}
      </div>
    );
  };

  // ── RENDER TAB CONTENT ────────────────────────────────────────────────────────
  const renderTabContent = () => {
    switch (activeTab) {
      case "home": return renderHome();
      case "chat": return renderChat();
      case "audit": return renderAudit();
      case "leaderboard": return renderLeaderboard();
      case "obsidian": return renderObsidian();
      default: return null;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // LAYOUT
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ ...DARK_BG, fontFamily: "'Cairo', sans-serif" }}
      dir="rtl"
    >
      {/* ── Top Bar ───────────────────────────────────────────────────────── */}
      <header
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{
          background: "rgba(15,1,2,0.9)",
          borderBottom: `1px solid ${BORDER}`,
          backdropFilter: "blur(10px)",
          position: "sticky",
          top: 0,
          zIndex: 50,
        }}
      >
        <div className="flex items-center gap-3">
          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg"
            style={{ color: GOLD }}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>
          <div>
            <span className="font-black text-lg" style={{ color: GOLD }}>
              HUJJAH Studio ✦
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {user && (
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm font-bold" style={{ color: "rgba(253,246,227,0.8)" }}>
                {user.name}
              </span>
              {roleBadge(user.studio_role || user.role)}
            </div>
          )}
          <button
            onClick={logout}
            className="text-xs font-bold px-3 py-2 rounded-lg transition-all"
            style={{
              background: "rgba(241,225,148,0.1)",
              border: `1px solid ${BORDER}`,
              color: "rgba(241,225,148,0.6)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(241,225,148,0.18)";
              e.currentTarget.style.color = GOLD;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(241,225,148,0.1)";
              e.currentTarget.style.color = "rgba(241,225,148,0.6)";
            }}
          >
            خروج
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* ── Sidebar (desktop always visible, mobile overlay) ─────────── */}
        <>
          {/* Mobile overlay */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black/60 z-30 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          <aside
            className={`
              fixed md:static top-0 right-0 h-full z-40
              transition-transform duration-300 ease-in-out
              md:translate-x-0
              ${sidebarOpen ? "translate-x-0" : "translate-x-full md:translate-x-0"}
              w-56 flex-shrink-0
            `}
            style={{
              background: SIDEBAR_BG,
              borderLeft: `1px solid ${BORDER}`,
              backdropFilter: "blur(12px)",
              paddingTop: "60px",
            }}
          >
            <nav className="p-3 space-y-1">
              {TABS.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setSidebarOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all duration-150 text-right"
                    style={{
                      background: active ? "rgba(241,225,148,0.15)" : "transparent",
                      color: active ? GOLD : "rgba(241,225,148,0.5)",
                      borderRight: active ? `3px solid ${GOLD}` : "3px solid transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = "rgba(241,225,148,0.08)";
                        e.currentTarget.style.color = "rgba(241,225,148,0.8)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "rgba(241,225,148,0.5)";
                      }
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Sidebar footer */}
            <div
              className="absolute bottom-4 left-0 right-0 px-4 text-center"
              style={{ color: "rgba(241,225,148,0.2)", fontSize: 11 }}
            >
              حُجّة © {new Date().getFullYear()}
            </div>
          </aside>
        </>

        {/* ── Main Content ──────────────────────────────────────────────── */}
        <main
          className="flex-1 overflow-y-auto p-4 sm:p-6"
          style={{ minWidth: 0 }}
        >
          <div className="max-w-4xl mx-auto">
            {renderTabContent()}
          </div>
        </main>
      </div>
    </div>
  );
}
